import BigNumber from "bignumber.js"
import moment from "moment"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { Button, Form, OverlayTrigger, Popover, Table } from "react-bootstrap"
import DatePicker from "react-datepicker"
import Select, { SingleValue } from "react-select"

import { VesselSegment } from "user/userService"
import { useFetchCargoesFromPort } from "../../hooks/useFetchCargoesFromPort"
import { useFetchDistance } from "../../hooks/useFetchDistance"
import { useFetchPortTurnAroundTime } from "../../hooks/useFetchPortTurnAroundTime"

import { useItineraryContext } from "modules/PortPredictor/hooks/itineraryContext"
import { useFetchPortsTerminals } from "../../hooks/useFetchPortsTerminals"
import {
  PortPredictorBerthsData,
  PortPredictorCargoGroupTypes,
  PortPredictorPortsTerminalsData,
  PortPredictorTurnAroundTimeData,
  PortPredictorVesselStateDataWithMajorCargo,
  VesselActivity,
} from "../../hooks/usePortPredictorAPI"
import {
  MarkerType,
  PortPredictorJourneyMarker,
  PortPredictorMarkerArrival,
  PortPredictorMarkerDeparture,
  PortType,
} from "../../portPredictorReducer"

import { PortPredictorDatePicker } from "./PortPredictorArrivalDatePicker"
import { PortPredictorMinMedianMaxButtons } from "./PortPredictorArrivalMinMedianMaxButtons"
import { PortPredictorPortsSelector } from "./PortPredictorArrivalPortsSelector"
import { PortPredictorCargoInputs } from "./PortPredictorCargoInputs"
import { PortPredictorDaysInPortInput } from "./PortPredictorDaysInPortInput"

import {
  calcDaysFromDeparture,
  findVesselPortType,
  getVesselStateForPortType,
} from "./helpers"
import { useFetchPortHolidays } from "modules/PortHolidays/useFetchPortHolidays"
import { findNearbyHolidaysForLaycan } from "./PortPredictorDisplayArrivalHelpers"
import { PortHoliday } from "modules/PortHolidays/usePortHolidayAPI"
import { getFootnote } from "../PortPredictorDatePickerHelpers"

export interface PortPredictorPortSelectOption
  extends PortPredictorPortsTerminalsData {
  label: string // Corresponds to port name
  value: string // Corresponds to port locode
}
export interface PortPredictorBerthSelectOption {
  label: string // Corresponds to terminal_name
  value: string // Corresponds to terminal_id
}

interface PortPredictorArrivalInputsProps {
  currMarker: PortPredictorMarkerArrival
  segment: VesselSegment
  prevMarker: PortPredictorMarkerDeparture | undefined
  nextMarker: PortPredictorMarkerDeparture | undefined
  isStartOfJourney: boolean
  turnAroundTimeData: PortPredictorTurnAroundTimeData[] | undefined
  onUpdateMarker: (args: PortPredictorJourneyMarker) => void
  onDeleteMarker: (args: PortPredictorJourneyMarker) => void
  setTurnAroundTimeData: Dispatch<
    SetStateAction<PortPredictorTurnAroundTimeData[] | undefined>
  >
  chosenVesselStateData: PortPredictorVesselStateDataWithMajorCargo | undefined
}

export interface PortPredictorPortTypeSelectOption {
  label: "Discharge Port" | "Load Port" | "Bunkering / Other"
  value: PortType
  activity: VesselActivity
}

export const portTypes: PortPredictorPortTypeSelectOption[] = [
  {
    label: "Discharge Port",
    value: PortType.Discharge,
    activity: VesselActivity.Discharge,
  },
  {
    label: "Load Port",
    value: PortType.Load,
    activity: VesselActivity.Load, // User to query Research API for turnAround time
  },
  {
    label: "Bunkering / Other",
    value: PortType.Other,
    activity: VesselActivity.Other,
  },
]

export function PortPredictorArrivalInputs({
  currMarker,
  segment,
  prevMarker,
  nextMarker,
  isStartOfJourney,
  onDeleteMarker,
  onUpdateMarker,
  setTurnAroundTimeData,
  turnAroundTimeData,
  chosenVesselStateData,
}: PortPredictorArrivalInputsProps) {
  const { getTabStateKeyValue } = useItineraryContext()
  const [chosenPort, setChosenPort] = useState<
    PortPredictorPortsTerminalsData | undefined
  >(currMarker.port)

  const [chosenBerth, setChosenBerth] = useState<
    PortPredictorBerthsData | undefined
  >(currMarker.berth)

  const [chosenPortType, setChosenPortType] = useState<
    PortPredictorPortTypeSelectOption | undefined
  >(findVesselPortType(currMarker.portType))

  const [turnAroundTime, setTurnAroundTime] = useState<number | undefined>(
    currMarker.turnAroundTime
  )

  const { portsTerminalsData, isPortsTerminalsLoading, isPortsTerminalsError } =
    useFetchPortsTerminals()

  const { cargoesData, isCargoesLoading, isCargoesError } =
    useFetchCargoesFromPort({
      segment,
      locode: chosenPort?.locode,
      activity: chosenPortType?.activity,
    })

  const [chosenCargo, setChosenCargo] = useState<{
    cargo: string | undefined
    cargoGroup: PortPredictorCargoGroupTypes | undefined
  }>({
    cargo: currMarker.cargo,
    cargoGroup: currMarker.cargoGroup,
  })

  const getHolidaysStart = currMarker.arrivalDate
    ? moment(currMarker.arrivalDate).startOf("year").format("YYYY-MM-DD")
    : moment().startOf("year").format("YYYY-MM-DD")

  const getHolidaysEnd = currMarker.arrivalDate
    ? moment(currMarker.arrivalDate)
        .add(1, "year")
        .endOf("year")
        .format("YYYY-MM-DD")
    : moment().add(1, "year").endOf("year").format("YYYY-MM-DD")

  const { portHolidaysData } = useFetchPortHolidays({
    locode: currMarker.port?.locode,
    startDate: getHolidaysStart,
    endDate: getHolidaysEnd,
  })

  const holidaysNearLaycan = findNearbyHolidaysForLaycan({
    triggerDate: currMarker.laycanStart,
    holidays: portHolidaysData?.holidays,
    rangeInDays: 4,
  })

  const {
    portTurnAroundTimeData,
    portTurnAroundTimeMinMedianMax,
    isPortTurnAroundTimeLoading,
    isPortTurnAroundTimeSuccess,
  } = useFetchPortTurnAroundTime({
    segment,
    cargo: chosenCargo.cargo,
    cargoGroup: chosenCargo.cargoGroup,
    locode: chosenPort?.locode,
    activity: chosenPortType?.activity,
    terminal_id: chosenPort?.terminal_id,
  })

  const { distanceInNm: distanceFromDeparture, isGetDistanceLoading } =
    useFetchDistance({
      source: prevMarker && (prevMarker as PortPredictorMarkerDeparture).posWkt,
      target: chosenPort?.pos_wkt,
    })

  const daysFromDeparture = calcDaysFromDeparture({
    departureMarker: prevMarker,
    distanceFromDeparture,
  })

  useEffect(() => {
    if (portTurnAroundTimeMinMedianMax?.median.value) {
      setTurnAroundTime(portTurnAroundTimeMinMedianMax.median.value)
      onUpdateMarker({
        ...currMarker,
        turnAroundTime: portTurnAroundTimeMinMedianMax.median.value,
      })
      return
    }
  }, [portTurnAroundTimeMinMedianMax?.median.value])

  useEffect(() => {
    if (!nextMarker?.date) {
      return
    }

    /**
     * Used when user manually inputs departure date in a marker below this currMarker
     * If not manually inputted, do not setTurnAroundTime here
     */
    if (!nextMarker?.departureDateManuallyUpdated) {
      return
    }
    const arrivalDate = moment(currMarker.arrivalDate)
    const departureDate = moment(nextMarker.date)

    const daysBeforeVesselDepartsPort = departureDate.diff(arrivalDate, "days")
    setTurnAroundTime(daysBeforeVesselDepartsPort)

    onUpdateMarker({
      ...currMarker,
      turnAroundTime: daysBeforeVesselDepartsPort,
      nextMarkerDepartureDateManuallyUpdated: true,
    })
  }, [
    nextMarker?.date,
    currMarker.arrivalDate,
    nextMarker?.departureDateManuallyUpdated,
  ])

  useEffect(() => {
    /*
    /* If you need to a state change consider if this can be done in the createMarkerBase 
    /* stage, or the useJourneyStart stage. Or in a seperate useEffect.
    */
    if (
      !prevMarker?.date ||
      !distanceFromDeparture ||
      !daysFromDeparture ||
      !chosenPort
    ) {
      return
    }

    if (
      chosenPort.locode === currMarker.port?.locode &&
      daysFromDeparture === currMarker.daysFromDeparture &&
      distanceFromDeparture === currMarker.distanceFromDeparture
    ) {
      return
    }

    onUpdateMarker({
      ...currMarker,
      daysFromDeparture,
      port: chosenPort,
      distanceFromDeparture: distanceFromDeparture,
      arrivalDate: moment(prevMarker.date)
        .add(daysFromDeparture, "days")
        .format("YYYY-MM-DD"),
    })
  }, [distanceFromDeparture, daysFromDeparture, chosenPort])

  useEffect(() => {
    /**
     * Pass portTurnAroundTimeData to parent so we can display it on timeline right side
     */

    if (!isPortTurnAroundTimeSuccess || !portTurnAroundTimeData) {
      return
    }

    setTurnAroundTimeData(portTurnAroundTimeData)
  }, [
    isPortTurnAroundTimeSuccess,
    portTurnAroundTimeData,
    setTurnAroundTimeData,

    turnAroundTimeData,
  ])

  useEffect(() => {
    const portType = findVesselPortType(currMarker.portType)
    setChosenPortType(portType)
  }, [currMarker.portType])

  return (
    <div className="PortPredictor_MarkerArrivalInputs">
      <div className="mb-3 PortPredictor_InputsContainer">
        {/* If currMarker.id === 0 and it is an Arrival Marker (hence we use this component)
        but the vessel's initial state is Open Sea, that means the user has "changed to port"
        */}
        {currMarker.id === 0 && (
          <div className="PortPredictor_InputWrapper mb-2">
            <Form.Label className="PortPredictor_FormLabel fw-bold">
              <>Arrival Date</>
            </Form.Label>
            <div className="PortPredictor_InputGroup PortPredictor_DatePicker_Wrapper">
              <DatePicker
                disabled={!getTabStateKeyValue("isEditMode")}
                dateFormat="dd/MM/yyyy"
                placeholderText="Enter Arrival Date"
                dayClassName={(date) =>
                  highlightDays({
                    date,
                    portHolidaysData: portHolidaysData?.holidays,
                    turnAroundTime,
                    arrivalDate: currMarker.arrivalDate,
                  })
                }
                selected={
                  currMarker.arrivalDate
                    ? new Date(currMarker.arrivalDate)
                    : null
                }
                onChange={(date) => {
                  if (!date) {
                    return
                  }

                  onUpdateMarker({
                    ...currMarker,
                    arrivalDate: moment(date).format("YYYY-MM-DD"),
                  })
                }}
              >
                <div className="PortPredictor_DatePicker_Footnote_Wrapper">
                  <div>
                    {getFootnote({
                      portHolidays: portHolidaysData?.holidays,
                      date: currMarker.arrivalDate
                        ? new Date(currMarker.arrivalDate)
                        : new Date(),
                    })}
                  </div>
                </div>
              </DatePicker>
            </div>
          </div>
        )}
        <div className="PortPredictor_InputWrapper">
          <Form.Label className="PortPredictor_FormLabel fw-bold">
            <>Port Type</>
          </Form.Label>
          <Select
            isDisabled={!getTabStateKeyValue("isEditMode")}
            className="PortPredictor_InputGroup"
            options={portTypes}
            value={chosenPortType}
            onChange={(
              portType: SingleValue<PortPredictorPortTypeSelectOption>
            ) => {
              if (!portType) {
                return
              }
              const newTurnAroundTime =
                portType.value === PortType.Other
                  ? undefined
                  : currMarker.turnAroundTime
              const updatedMarker = {
                ...currMarker,
                portType: portType.value as PortType,
                laycanStart: "",
                laycanEnd: "",
                turnAroundTime: newTurnAroundTime,
                vesselState: getVesselStateForPortType({
                  chosenPortType: portType,
                  currMarkerVesselState: currMarker.vesselState,
                  previousMarkerVesselState: prevMarker?.vesselState,
                }),
                nextMarkerDepartureDateManuallyUpdated: false,
              }

              onUpdateMarker(updatedMarker)
              setChosenPortType(portType)
              setTurnAroundTime(newTurnAroundTime)
            }}
          />
        </div>
        <div className="PortPredictor_InputWrapper mt-2">
          <PortPredictorPortsSelector
            isPortsError={isPortsTerminalsError}
            isPortsLoading={isPortsTerminalsLoading}
            portsData={portsTerminalsData}
            chosenPortType={chosenPortType}
            chosenPort={chosenPort}
            chosenCargo={chosenCargo.cargo}
            isPortTurnAroundTimeLoading={isPortTurnAroundTimeLoading}
            chosenVesselStateData={chosenVesselStateData}
            setChosenPort={(
              port: PortPredictorPortsTerminalsData | undefined
            ) => {
              if (!port) {
                return
              }

              setChosenPort(port)
              setChosenBerth(undefined)

              onUpdateMarker({
                ...currMarker,
                port,
                berth: undefined,
              })
            }}
          />
        </div>
        {chosenPort &&
          chosenCargo &&
          !portTurnAroundTimeMinMedianMax &&
          !isPortTurnAroundTimeLoading &&
          chosenPort &&
          chosenCargo &&
          !isPortTurnAroundTimeLoading &&
          currMarker.portType !== PortType.Other && (
            <div className="PortPredictor_InputWrapper">
              No vessels found{" "}
              {chosenPortType?.activity === VesselActivity.Discharge
                ? "discharging"
                : "loading"}{" "}
              {chosenCargo.cargo} at {chosenPort?.port}
            </div>
          )}
        {chosenPortType && chosenPortType.value === PortType.Other && (
          <div className="PortPredictor_InputWrapper mt-2">
            <PortPredictorDaysInPortInput
              portTurnAroundTime={turnAroundTime ? String(turnAroundTime) : ""}
              setPortTurnAroundTime={(time) => {
                if (time === "") {
                  return
                }

                setTurnAroundTime(Number(time))

                onUpdateMarker({
                  ...currMarker,
                  berth: chosenBerth,
                  markerType: MarkerType.Arrival,
                  turnAroundTime: Number(time),
                  nextMarkerDepartureDateManuallyUpdated: false,
                })
              }}
            />
          </div>
        )}
        {chosenPortType && chosenPortType.value !== PortType.Other && (
          <>
            <div className="PortPredictor_InputWrapper mt-2">
              <PortPredictorCargoInputs
                isDisabled={chosenPort === undefined}
                cargoesData={cargoesData}
                isCargoesLoading={isCargoesLoading}
                isCargoesError={isCargoesError}
                chosenCargo={chosenCargo}
                setChosenCargo={(chosenCargo) => {
                  if (
                    !chosenCargo ||
                    !chosenCargo.cargo ||
                    !chosenCargo.cargoGroup
                  ) {
                    return
                  }

                  onUpdateMarker({
                    ...currMarker,
                    cargo: chosenCargo.cargo,
                    cargoGroup: chosenCargo.cargoGroup,
                  })
                  setChosenCargo({
                    cargo: chosenCargo.cargo,
                    cargoGroup: chosenCargo.cargoGroup,
                  })
                }}
              />
            </div>
            {(chosenPortType.value === PortType.Load ||
              chosenPortType.value === PortType.Discharge) && (
              <div className="PortPredictor_InputWrapper mt-2 pt-1">
                <PortPredictorDatePicker
                  currMarkerLaycanStart={currMarker.laycanStart}
                  currMarkerLaycanEnd={currMarker.laycanEnd}
                  portHolidays={portHolidaysData?.holidays}
                  errors={portHolidaysData?.errors}
                  arrivalDate={currMarker.arrivalDate}
                  turnAroundTime={turnAroundTime}
                  updateLaycan={({
                    date,
                    isStart,
                  }: {
                    date: Date
                    isStart: boolean
                  }) => {
                    const key = isStart ? "laycanStart" : "laycanEnd"

                    onUpdateMarker({
                      ...currMarker,
                      [key]: date.toISOString(),
                    })
                  }}
                  chosenPortType={chosenPortType}
                />
                {holidaysNearLaycan && holidaysNearLaycan.length > 0 && (
                  <div className="PortPredictor_InputAlert">
                    <OverlayTrigger
                      placement="bottom"
                      overlay={renderLaycanHolidayWarning()}
                    >
                      <i className="ti-alert ms-1 d-flex align-items-center PortPredictor_AlertWarning" />
                    </OverlayTrigger>
                  </div>
                )}
              </div>
            )}
            <div>
              <div className="PortPredictor_InputWrapper mt-2">
                <PortPredictorDaysInPortInput
                  portTurnAroundTime={
                    turnAroundTime === undefined ? "" : String(turnAroundTime)
                  }
                  setPortTurnAroundTime={(time) => {
                    if (time === "") {
                      return
                    }

                    setTurnAroundTime(Number(time))

                    onUpdateMarker({
                      ...currMarker,
                      berth: chosenBerth,
                      markerType: MarkerType.Arrival,
                      turnAroundTime: Number(time),
                      nextMarkerDepartureDateManuallyUpdated: false,
                    })
                  }}
                />
              </div>
              {portTurnAroundTimeMinMedianMax &&
                chosenPort &&
                chosenCargo.cargo && (
                  <>
                    <div className="PortPredictor_InputWrapper mt-2">
                      <PortPredictorMinMedianMaxButtons
                        segment={segment}
                        portType={chosenPortType}
                        cargo={chosenCargo.cargo}
                        portName={chosenPort.port}
                        portTurnAroundTimeMinMedianMax={
                          portTurnAroundTimeMinMedianMax
                        }
                        portTurnAroundTime={turnAroundTime}
                        setPortTurnAroundTime={(time: number) => {
                          setTurnAroundTime(time)

                          onUpdateMarker({
                            ...currMarker,
                            berth: chosenBerth,
                            markerType: MarkerType.Arrival,
                            turnAroundTime: Number(time),
                            nextMarkerDepartureDateManuallyUpdated: false,
                          })
                        }}
                      />
                    </div>
                  </>
                )}
            </div>
          </>
        )}
        {!isStartOfJourney && (
          <div className="PortPredictor_DeleteMarkerButtonWrapper mt-1 mb-2">
            <Button
              disabled={!getTabStateKeyValue("isEditMode")}
              className="PortPredictor_DeleteMarkerButton"
              variant="outline-danger"
              size="sm"
              onClick={() => {
                onDeleteMarker({
                  ...currMarker,
                })
              }}
            >
              <i className="ti-trash me-1 PortPredictor_DeleteMarkerButton_Icon" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function getDepartureDate({
  arrivalDate,
  turnAroundTime,
}: {
  arrivalDate: string | undefined
  turnAroundTime: number | undefined
}) {
  if (!arrivalDate || !turnAroundTime) {
    return undefined
  }
  const portTurnAroundTimeRoundedUp = BigNumber(turnAroundTime)
    .decimalPlaces(0, BigNumber.ROUND_CEIL)
    .toNumber()
  return moment(arrivalDate)
    .add(portTurnAroundTimeRoundedUp, "days")
    .format("YYYY-MM-DD")
}

function renderLaycanHolidayWarning() {
  return (
    <Popover id="cargo-disclaimer">
      <Popover.Body>
        <div>
          <span style={{ fontWeight: "bold" }}>Holiday Alert</span>: Refer to
          calendar and holiday list. Possible delays or limited port operations.
          Consider adjusting plans.
        </div>
      </Popover.Body>
    </Popover>
  )
}

export function getHolidayDates(holidays: PortHoliday[] | undefined): Date[] {
  if (!holidays) {
    return []
  }
  const holidayDates: Date[] = []

  holidays.forEach((holiday) => {
    const startDate = moment(holiday.start_date)
    const endDate = moment(holiday.end_date)

    // Generate all dates in the range
    for (
      let currentDate = startDate.clone();
      currentDate.isSameOrBefore(endDate, "day");
      currentDate.add(1, "day")
    ) {
      holidayDates.push(currentDate.toDate())
    }
  })

  return holidayDates
}

export function highlightDays({
  date,
  portHolidaysData,
  turnAroundTime,
  arrivalDate,
  departureDate,
}: {
  date: Date
  portHolidaysData: PortHoliday[] | undefined
  turnAroundTime?: number
  arrivalDate: string | undefined
  departureDate?: string
}) {
  const holidayDates = getHolidayDates(portHolidaysData)
  const dates = holidayDates.map((date) => date.toDateString()) // "Sat Sep 07 2024"
  const holidaySet = new Set(dates)

  const derivedDepartureDate = !departureDate
    ? getDepartureDate({
        arrivalDate: arrivalDate,
        turnAroundTime,
      })
    : departureDate

  let css = ""
  const eta = arrivalDate ? moment(arrivalDate) : null
  const etd = derivedDepartureDate ? moment(derivedDepartureDate) : null
  const currentDate = moment(date)

  const isEtaToEtd = eta && etd && currentDate.isBetween(eta, etd, "day", "[]")

  if (isEtaToEtd) {
    css = css + "react-datepicker__isEtaEtd "
  }

  if (holidaySet.has(date.toDateString())) {
    css = css + "react-datepicker__holiday"
  }
  return css.trim()
}
